import { AlertDialog, BodyText, Button, Tooltip, toast } from "@/components/ui";
import { formatDateTime } from "@/lib/time";
// import { MiscellaneousNoteForm } from "./MiscellaneousNoteForm";
import type { Procedure } from "@curai/js-fhir-tools";
import { useParams } from "@tanstack/react-router";
import { Archive, Trash2 } from "lucide-react";
import { useState } from "react";
import { EditableRecordButton } from "../../components/EditableRecordButton";
import {
  useResolveMiscellaneousNote,
  useUpdateMiscellaneousNote,
  useVoidMiscellaneousNote,
} from "../network";
import { MiscellaneousNoteForm } from "./MiscellaneousNoteForm";

export const MiscellaneousNoteItem = ({
  id,
  performedDateTime,
  note,
  recorder,
}: Pick<Procedure, "id" | "performedDateTime" | "note" | "recorder">) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const [editing, setEditing] = useState(false);
  const [showVoidPrompt, setShowVoidPrompt] = useState(false);

  const {
    mutate: updateMiscellaneousNote,
    isPending: isUpdatingMiscellaneousNote,
  } = useUpdateMiscellaneousNote({
    procedureId: id as string,
    patientId: patientId as string,
    onSuccess: () => {
      toast.success("Miscellaneous note updated successfully");
      setEditing(false);
    },
  });

  const {
    mutate: resolveMiscellaneousNote,
    isPending: isResolvingMiscellaneousNote,
  } = useResolveMiscellaneousNote({
    procedureId: id as string,
    patientId: patientId as string,
    onSuccess: () => {
      toast.success("Miscellaneous note resolved successfully");
    },
  });

  const {
    mutate: voidMiscellaneousNote,
    isPending: isVoidingMiscellaneousNote,
  } = useVoidMiscellaneousNote({
    procedureId: id as string,
    patientId: patientId as string,
    onSuccess: () => {
      toast.success("Miscellaneous note voided successfully");
    },
  });

  const handleVoidMiscellaneousNote = () => {
    setShowVoidPrompt(false);
    voidMiscellaneousNote();
  };

  return editing ? (
    <MiscellaneousNoteForm
      noteId={id}
      text={note?.[0]?.text}
      onCancel={() => {
        setEditing(false);
      }}
      onSubmit={(data) => {
        updateMiscellaneousNote({
          note: data.text,
        });
      }}
      isSubmitting={isUpdatingMiscellaneousNote}
    />
  ) : (
    <>
      <EditableRecordButton
        onClick={() => {
          setEditing(true);
        }}
        containerClassName="w-full m-1"
      >
        <div className="flex flex-col gap-1 w-full">
          <BodyText size="sm">
            {note?.[0]?.text || "No content available"}
          </BodyText>
          <BodyText size="xs" textColor="secondary">
            {recorder?.display} on {formatDateTime(performedDateTime)}
          </BodyText>
          <div className="absolute flex top-1 right-1 group-hover:opacity-100 opacity-0 transition-opacity">
            <Tooltip side="bottom" tooltipContentText="Resolve">
              <Button
                variant="icon"
                className="mr-3 p-0 flex"
                onClick={(e) => {
                  e.stopPropagation();
                  resolveMiscellaneousNote({});
                }}
                disabled={isResolvingMiscellaneousNote}
                isLoading={isResolvingMiscellaneousNote}
                data-testid="resolve-button"
              >
                <Archive className="stroke-link h-4 w-4" size={12} />
              </Button>
            </Tooltip>
            <Tooltip side="bottom" tooltipContentText="Delete">
              <Button
                variant="icon"
                className="m-0 p-0 flex"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowVoidPrompt(true);
                }}
                disabled={isVoidingMiscellaneousNote}
                isLoading={isVoidingMiscellaneousNote}
                data-testid="delete-button"
              >
                <Trash2 className="stroke-link h-4 w-4" size={12} />
              </Button>
            </Tooltip>
          </div>
        </div>
      </EditableRecordButton>
      <AlertDialog
        isOpen={showVoidPrompt}
        onOpenChange={setShowVoidPrompt}
        isControlled
        title="Delete Miscellaneous Note"
        body={`Are you sure you want to delete this note? This will remove it entirely from the patient's medical record, and should only be used if the note was added in error (i.e. the wrong patient).`}
        actionLabel="Void"
        cancelLabel="Cancel"
        onAction={handleVoidMiscellaneousNote}
        onCancel={() => {
          setShowVoidPrompt(false);
        }}
        // TODO: Remove this once we fix the AlertDialogProvider
        // biome-ignore lint/complexity/noUselessFragments: We need to pass a trigger element to the AlertDialog so the AlertDialogProvider doesn't render the default trigger element
        triggerElement={<></>}
      />
    </>
  );
};
